import React, { useState } from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { motion } from 'framer-motion';
import { Calendar, AlertTriangle, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
export function PaymentFlexibility() {
  const {
    toast
  } = useToast();
  const [selectedWindow, setSelectedWindow] = useState('1-5');
  const handleSaveWindow = () => {
    toast.success('Payment window updated successfully!');
  };
  const handleSkipRequest = () => {
    toast.success('Skip request submitted for review!');
  };
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-16 lg:pb-0" data-id="element-4736">
      <Sidebar data-id="element-4737" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-4738">
        <DashboardHeader data-id="element-4739" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-4740">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-4741">
            <div className="mb-6 lg:mb-8" data-id="element-4742">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate" data-id="element-4743">
                Payment Flexibility
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-4744">
                Manage your payment schedule to fit your cash flow.
              </p>
            </div>

            <div className="space-y-6 lg:space-y-8" data-id="element-4745">
              <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 transition-colors" data-id="element-4746">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4" data-id="element-4747">
                  <div className="min-w-0" data-id="element-4748">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 truncate" data-id="element-4749">
                      <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-4750" />
                      <span className="truncate" data-id="element-4751">Select Payment Window</span>
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2" data-id="element-4752">
                      Choose a 5-day window to make your monthly payment.
                    </p>
                  </div>
                  <button onClick={handleSaveWindow} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 rounded-lg transition-colors whitespace-nowrap flex-shrink-0" data-id="element-4753">
                    Save Preference
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-4754">
                  {['1-5', '15-20', '25-30'].map(window => <button key={window} onClick={() => setSelectedWindow(window)} className={`p-4 rounded-lg border-2 text-left transition-all ${selectedWindow === window ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-600 dark:ring-blue-400' : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`} data-id="element-4755">
                      <div className="flex justify-between items-center mb-2" data-id="element-4756">
                        <span className="font-bold text-gray-900 dark:text-white" data-id="element-4757">
                          {window}th
                        </span>
                        {selectedWindow === window && <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-4758" />}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4759">
                        of every month
                      </p>
                    </button>)}
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 transition-colors" data-id="element-4760">
                <div className="mb-6" data-id="element-4761">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 truncate" data-id="element-4762">
                    <AlertTriangle className="h-5 w-5 text-orange-500 dark:text-orange-400 flex-shrink-0" data-id="element-4763" />
                    <span className="truncate" data-id="element-4764">Emergency Skip Month</span>
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2" data-id="element-4765">
                    Defer one month's rent in case of emergency. (1 per year)
                  </p>
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-100 dark:border-orange-800 mb-6 transition-colors" data-id="element-4766">
                  <div className="flex gap-3" data-id="element-4767">
                    <Clock className="h-5 w-5 text-orange-400 dark:text-orange-500 flex-shrink-0 mt-0.5" data-id="element-4768" />
                    <div className="min-w-0" data-id="element-4769">
                      <h3 className="text-sm font-medium text-orange-800 dark:text-orange-300 truncate" data-id="element-4770">
                        How it works
                      </h3>
                      <p className="mt-2 text-sm text-orange-700 dark:text-orange-400 break-words" data-id="element-4771">
                        If approved, your next payment will be skipped. The
                        amount will be spread equally across your remaining
                        lease months.
                      </p>
                    </div>
                  </div>
                </div>

                <button onClick={handleSkipRequest} className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-800 transition-colors" data-id="element-4772">
                  Request Skip Month
                  <ArrowRight className="ml-2 h-4 w-4 flex-shrink-0" data-id="element-4773" />
                </button>
              </section>
            </div>
          </motion.div>
        </div>
      </main>
    </div>;
}